import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase, readableError } from '../../lib/supabase'
import PortalFrame from '../../components/PortalFrame'
import { Spinner } from '../../components/ui'
import { useSession } from '../../lib/session'

/**
 * The owner's business dashboard — one superadmin account, one screen for
 * "how is this actually going". Everything else in /admin answers a church's
 * question; this one answers Tom's.
 *
 * Deliberately a registry of cards plus an ordering the owner controls,
 * rather than a fixed layout. What matters this week (money, storage, a
 * launching church) is not what mattered last week, and a screen he can't
 * rearrange is a screen he stops opening.
 *
 * Cards are also grouped (money/growth/operations/risk) so the four things
 * this screen answers stay visually separate even as the registry grows —
 * an owner scanning for "am I losing money" shouldn't have to skip past a
 * church roster to find the ledger.
 */

const CURRENCY = (code) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: code || 'AUD' })
/* Postgres bigint can arrive over PostgREST as a STRING, not a number. Division
   coerces on its own, but `+` would concatenate — "0" + "1500" = "01500" — so
   every cents value is pushed through this before any arithmetic. Cheap, and it
   removes a whole class of silently wrong money. */
const n = (v) => Number(v ?? 0) || 0
const money = (cents, code) => CURRENCY(code).format(n(cents) / 100)
const gb = (bytes) => n(bytes) / 1e9
const fmtGb = (bytes) => `${gb(bytes).toFixed(2)} GB`

const ago = (iso) => {
  if (!iso) return null
  const h = Math.floor((Date.now() - new Date(iso)) / 3.6e6)
  if (h < 1) return 'just now'
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}
const daysAgo = (iso) => (iso ? Math.floor((Date.now() - new Date(iso)) / 8.64e7) : null)

const KIND_LABEL = { revenue: 'Revenue', cost: 'Cost' }
const CATEGORIES = ['subscriptions', 'services', 'infrastructure', 'software', 'contractors', 'marketing', 'other']
const RECURRING = ['once', 'monthly', 'yearly']

const LOG_CATEGORIES = [
  ['registration', 'Registration'], ['tax', 'Tax'], ['legal', 'Legal'], ['finance', 'Finance'],
  ['banking', 'Banking'], ['insurance', 'Insurance'], ['milestone', 'Milestone'],
  ['incident', 'Incident'], ['decision', 'Decision'], ['other', 'Other'],
]
const LOG_LABEL = Object.fromEntries(LOG_CATEGORIES)

/* Section registry. Order here is the render order — money first, risk last —
   and is fixed regardless of the owner's per-card arrangement, so "which
   quadrant of the business is this" never depends on what's been dragged
   around. Per-card order inside a group still comes from `layout`. */
const GROUPS = [
  { id: 'money', title: 'Money' },
  { id: 'growth', title: 'Growth' },
  { id: 'operations', title: 'Operations' },
  { id: 'risk', title: 'Risk' },
]

/* ============================================================
   Card bodies. Each takes the whole load() result plus the bits
   of state and mutators it owns — nothing fetches on its own,
   so rearranging or hiding a card never triggers a network call.
   ============================================================ */

function OverviewCard({ overview }) {
  if (!overview) return <p className="bz-meta">No overview yet.</p>
  return (
    <>
      <div className="bz-stats">
        {/* The one accent tile in this card — "new this week" is the number
            that actually moves week to week, everything else is a total. */}
        <div className="bz-stat bz-stat-accent">
          <span className="bz-stat-n">{overview.users_new_7d}</span>
          <span className="bz-stat-l">new users this week</span>
        </div>
        <div className="bz-stat"><span className="bz-stat-n">{overview.users_total}</span><span className="bz-stat-l">users</span></div>
        <div className="bz-stat"><span className="bz-stat-n">{overview.churches_total}</span><span className="bz-stat-l">churches</span></div>
        <div className="bz-stat"><span className="bz-stat-n">{overview.songs_total}</span><span className="bz-stat-l">songs</span></div>
      </div>
      <p className="bz-meta" style={{ marginTop: 10 }}>
        <b className={overview.songs_in_review ? 'bad' : 'good'}>{overview.songs_in_review}</b> in
        review · <b className={overview.members_pending ? 'bad' : 'good'}>{overview.members_pending}</b>{' '}
        pending members
        {overview.users_suspended > 0 && <> · <b className="bad">{overview.users_suspended}</b> suspended</>}
      </p>
    </>
  )
}

function MoneyCard({ business, settings, onSetPrice }) {
  const [dollars, setDollars] = useState('')
  const [busy, setBusy] = useState(false)
  if (!business) return <p className="bz-meta">No business figures yet.</p>
  const code = business.currency || 'AUD'
  const pl = n(business.revenue_mtd_cents) - n(business.costs_mtd_cents)
  const priceUnset = !settings.plus_price_cents

  const save = async () => {
    const cents = Math.round(parseFloat(dollars || '0') * 100)
    if (!cents || cents < 0) return
    setBusy(true)
    await onSetPrice(cents)
    setBusy(false)
    setDollars('')
  }

  return (
    <>
      <p className="bz-lede">
        <b>{money(business.mrr_cents, code)}</b> MRR · <b>{business.paying_subscribers}</b> paying ·{' '}
        revenue <b>{money(business.revenue_mtd_cents, code)}</b> MTD · costs{' '}
        <b>{money(business.costs_mtd_cents, code)}</b> MTD
      </p>
      <p className="bz-meta">
        Profit/loss MTD: <b className={pl >= 0 ? 'good' : 'bad'}>{money(pl, code)}</b> · run rate{' '}
        {money(business.monthly_run_rate_cents, code)}/mo
      </p>
      {priceUnset && (
        <div className="bz-inline">
          <p className="bz-meta bad">No plan price set — MRR reads zero until you set one.</p>
          <input className="bz-input bz-num" type="number" min="0" step="0.01" placeholder="$/month"
                 value={dollars} onChange={(e) => setDollars(e.target.value)} />
          <button className="bz-btn" disabled={busy || !dollars} onClick={save}>
            {busy ? 'Saving…' : 'Set price'}
          </button>
        </div>
      )}
    </>
  )
}

function LedgerCard({ entries, currency, onAdd, onDelete }) {
  const [form, setForm] = useState({
    entry_date: new Date().toISOString().slice(0, 10), kind: 'cost', category: 'infrastructure',
    vendor: '', description: '', amount: '', recurring: 'once', note: '',
  })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  const submit = async (e) => {
    e.preventDefault()
    setErr(null)
    const cents = Math.round(parseFloat(form.amount || '0') * 100)
    if (!form.description.trim()) return setErr('Description is required.')
    if (!cents || cents < 0) return setErr('Amount must be a positive number.')
    setBusy(true)
    const { error } = await onAdd({
      entry_date: form.entry_date, kind: form.kind, category: form.category,
      vendor: form.vendor || null, description: form.description.trim(),
      amount_cents: cents, recurring: form.recurring, note: form.note || null,
    })
    setBusy(false)
    if (error) return setErr(readableError(error))
    setForm((f) => ({ ...f, vendor: '', description: '', amount: '', note: '' }))
  }

  const totalRevenue = entries.filter((e) => e.kind === 'revenue').reduce((s, e) => s + n(e.amount_cents), 0)
  const totalCost = entries.filter((e) => e.kind === 'cost').reduce((s, e) => s + n(e.amount_cents), 0)

  return (
    <>
      <form className="bz-ledgerform" onSubmit={submit}>
        <div className="bz-row2">
          <input className="bz-input" type="date" value={form.entry_date} onChange={set('entry_date')} />
          <select className="bz-input" value={form.kind} onChange={set('kind')}>
            <option value="cost">Cost</option>
            <option value="revenue">Revenue</option>
          </select>
        </div>
        <div className="bz-row2">
          <select className="bz-input" value={form.category} onChange={set('category')}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="bz-input" value={form.recurring} onChange={set('recurring')}>
            {RECURRING.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <input className="bz-input" placeholder="Vendor (optional)" value={form.vendor} onChange={set('vendor')} />
        <input className="bz-input" placeholder="Description" maxLength={200}
               value={form.description} onChange={set('description')} />
        <div className="bz-row2">
          <input className="bz-input" type="number" min="0" step="0.01" placeholder="Amount ($)"
                 value={form.amount} onChange={set('amount')} />
          <button className="bz-btn bz-primary" disabled={busy}>{busy ? 'Adding…' : 'Add entry'}</button>
        </div>
        {err && <p className="err">{err}</p>}
      </form>

      <p className="bz-meta" style={{ marginTop: 10 }}>
        Last 20: <b className="good">{money(totalRevenue, currency)}</b> revenue ·{' '}
        <b className="bad">{money(totalCost, currency)}</b> cost
      </p>
      {entries.length === 0 ? (
        <p className="bz-meta">No ledger entries yet.</p>
      ) : (
        entries.map((e) => (
          <div key={e.id} className="bz-ledgerrow">
            <span className="bz-meta">{e.entry_date} · {e.category}{e.vendor ? ` · ${e.vendor}` : ''}</span>
            <span className="bz-ledgerdesc">{e.description}</span>
            <span className={KIND_LABEL[e.kind] === 'Revenue' ? 'good' : 'bad'}>{money(e.amount_cents, currency)}</span>
            <button className="bz-btn" onClick={() => onDelete(e.id)}>Delete</button>
          </div>
        ))
      )}
    </>
  )
}

/* Recurring costs feed the forecast card without a schema change —
   business_ledger.recurring already distinguishes monthly/yearly/once, this
   just totals what's already there. No growth curve is modelled: MRR is
   held flat in the projection because a made-up growth assumption would
   read as more certain than the number actually is. */
function ForecastCard({ recurringCosts, currency, mrrCents }) {
  const monthly = recurringCosts.filter((e) => e.recurring === 'monthly')
  const yearly = recurringCosts.filter((e) => e.recurring === 'yearly')
  const monthlyTotal = monthly.reduce((s, e) => s + n(e.amount_cents), 0)
  const yearlyTotal = yearly.reduce((s, e) => s + n(e.amount_cents), 0)
  const yearlyAsMonthly = yearlyTotal / 12
  const runRate = monthlyTotal + yearlyAsMonthly
  const annualised = runRate * 12
  const margin = n(mrrCents) - runRate

  const now = new Date()
  const rows = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() + i + 1, 1)
    return { label: d.toLocaleString('en-AU', { month: 'short', year: 'numeric' }), cost: runRate, revenue: n(mrrCents) }
  })
  let cumulative = 0
  const projection = rows.map((r) => {
    cumulative += r.revenue - r.cost
    return { ...r, cumulative }
  })

  return (
    <>
      <p className="bz-lede">
        <b>{money(runRate, currency)}</b>/mo run rate · <b>{money(annualised, currency)}</b> annualised
      </p>
      <p className="bz-meta">
        Monthly margin (MRR − run rate): <b className={margin >= 0 ? 'good' : 'bad'}>{money(margin, currency)}</b>/mo
      </p>

      <h4 className="bz-title" style={{ marginTop: 14, fontSize: 13.5 }}>12-month projection (assumes today's MRR, flat)</h4>
      <div className="bz-forecasttable">
        {projection.map((r) => (
          <div key={r.label} className="bz-ledgerrow">
            <span className="bz-ledgerdesc">{r.label}</span>
            <span className="bz-meta">cost {money(r.cost, currency)}</span>
            <span className="bz-meta">revenue {money(r.revenue, currency)}</span>
            <span className={r.cumulative >= 0 ? 'good' : 'bad'}>cum. {money(r.cumulative, currency)}</span>
          </div>
        ))}
      </div>

      <h4 className="bz-title" style={{ marginTop: 14, fontSize: 13.5 }}>Recurring costs</h4>
      {recurringCosts.length === 0 ? (
        <p className="bz-meta">No recurring costs entered yet.</p>
      ) : (
        recurringCosts.map((e) => (
          <div key={e.id} className="bz-ledgerrow">
            <span className="bz-ledgerdesc">
              {e.vendor ? <b>{e.vendor}</b> : null} {e.vendor ? '— ' : ''}{e.description}
            </span>
            <span className="bz-meta">{money(e.amount_cents, currency)} / {e.recurring}</span>
          </div>
        ))
      )}
    </>
  )
}

function LogCard({ entries, onAdd, onDelete }) {
  const [form, setForm] = useState({
    occurred_on: new Date().toISOString().slice(0, 10), category: 'registration',
    title: '', reference: '', detail: '',
  })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  const submit = async (e) => {
    e.preventDefault()
    setErr(null)
    if (!form.title.trim()) return setErr('Title is required.')
    setBusy(true)
    const { error } = await onAdd({
      occurred_on: form.occurred_on, category: form.category, title: form.title.trim(),
      reference: form.reference || null, detail: form.detail || null,
    })
    setBusy(false)
    if (error) return setErr(readableError(error))
    setForm((f) => ({ ...f, title: '', reference: '', detail: '' }))
  }

  return (
    <>
      <form className="bz-ledgerform" onSubmit={submit}>
        <div className="bz-row2">
          <input className="bz-input" type="date" value={form.occurred_on} onChange={set('occurred_on')} />
          <select className="bz-input" value={form.category} onChange={set('category')}>
            {LOG_CATEGORIES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
        </div>
        <input className="bz-input" placeholder="Title" maxLength={160} value={form.title} onChange={set('title')} />
        <input className="bz-input" placeholder="Reference (optional)" maxLength={120}
               value={form.reference} onChange={set('reference')} />
        {/* An Australian TFN is regulated separately under the Privacy (Tax File
            Number) Rule and must never sit in a general-purpose log table. The
            warning lives here, at the point of entry, because documentation
            nobody reads doesn't stop someone pasting one into a reference field. */}
        <p className="bz-meta">
          An ABN or invoice number is fine here. Never a TFN — a Tax File Number is regulated
          separately and must not be kept in a general log.
        </p>
        <textarea className="bz-input" placeholder="Detail (optional)" maxLength={4000} rows={3}
                  value={form.detail} onChange={set('detail')} />
        <button className="bz-btn bz-primary" disabled={busy}>{busy ? 'Adding…' : 'Add entry'}</button>
        {err && <p className="err">{err}</p>}
      </form>

      {entries.length === 0 ? (
        <p className="bz-meta" style={{ marginTop: 10 }}>No log entries yet.</p>
      ) : (
        entries.map((e) => (
          <div key={e.id} className="bz-ledgerrow" style={{ marginTop: 10 }}>
            <span className="bz-meta">{e.occurred_on} · <span className="bz-tag">{LOG_LABEL[e.category] || e.category}</span></span>
            <span className="bz-ledgerdesc">
              <b>{e.title}</b>{e.reference ? ` — ${e.reference}` : ''}
              {e.detail ? <span className="bz-rowsub" style={{ display: 'block' }}>{e.detail}</span> : null}
            </span>
            <button className="bz-btn" onClick={() => onDelete(e.id)}>Delete</button>
          </div>
        ))
      )}
    </>
  )
}

function OpsCard({ snapshot }) {
  if (!snapshot) {
    return (
      <p className="bz-meta">
        No operations snapshot yet — the nightly overwatch agent writes this table, and the first
        one will appear after its next run.
      </p>
    )
  }
  const p = snapshot.payload || {}
  const cronActive = p.cron_jobs_active
  const cronTotal = p.cron_jobs_total
  const cronDown = cronActive != null && cronTotal != null && n(cronActive) < n(cronTotal)

  return (
    <>
      <div className="bz-stats">
        <div className="bz-stat">
          <span className="bz-stat-n">{p.edge_functions_active ?? '—'}/{p.edge_functions_total ?? '—'}</span>
          <span className="bz-stat-l">edge functions</span>
        </div>
        <div className={`bz-stat${cronDown ? ' bad' : ''}`}>
          <span className="bz-stat-n">{p.cron_jobs_active ?? '—'}/{p.cron_jobs_total ?? '—'}</span>
          <span className="bz-stat-l">cron jobs</span>
        </div>
        <div className="bz-stat"><span className="bz-stat-n">{p.migrations ?? '—'}</span><span className="bz-stat-l">migrations</span></div>
        <div className="bz-stat"><span className="bz-stat-n">{p.db_size ?? '—'}</span><span className="bz-stat-l">database size</span></div>
      </div>
      {cronDown && (
        <p className="bz-meta bad" style={{ marginTop: 10 }}>
          {n(cronTotal) - n(cronActive)} scheduled job(s) switched off
        </p>
      )}
      {p.latest_migration && <p className="bz-meta">Latest migration: {p.latest_migration}</p>}
      {p.issues_24h != null && (
        <p className="bz-meta">
          Issues in last 24h: <b className={n(p.issues_24h) > 0 ? 'bad' : 'good'}>{p.issues_24h}</b>
        </p>
      )}
      {Array.isArray(p.cron_job_names) && p.cron_job_names.length > 0 && (
        <div className="bz-inline" style={{ marginTop: 8, flexWrap: 'wrap' }}>
          {p.cron_job_names.map((name) => <span key={name} className="bz-tag">{name}</span>)}
        </div>
      )}
      <p className="bz-meta" style={{ marginTop: 10 }}>Observed {ago(snapshot.observed_at) || '—'}</p>
    </>
  )
}

function StorageCard({ overview, settings, onSaveLimits }) {
  const [limitGb, setLimitGb] = useState(String(settings.storage_limit_gb ?? ''))
  const [warnPct, setWarnPct] = useState(String(settings.storage_warn_pct ?? ''))
  const [busy, setBusy] = useState(false)
  const used = gb(overview?.storage_bytes)
  const limit = n(settings.storage_limit_gb)
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0
  const warn = n(settings.storage_warn_pct) || 75
  const tone = pct >= warn ? 'bad' : 'good'

  const save = async () => {
    setBusy(true)
    await onSaveLimits({ storage_limit_gb: Number(limitGb) || 0, storage_warn_pct: Number(warnPct) || 0 })
    setBusy(false)
  }

  return (
    <>
      <p className="bz-lede">
        <b className={tone}>{used.toFixed(2)} GB</b> used of <b>{limit || '—'} GB</b>
        {limit > 0 && <> · <b>{(limit - used).toFixed(2)} GB</b> remaining</>}
      </p>
      {limit > 0 && (
        <div className="bz-bar">
          <div className={`bz-barfill ${tone}`} style={{ width: `${pct}%` }} />
        </div>
      )}
      <div className="bz-inline" style={{ marginTop: 12 }}>
        <input className="bz-input bz-num" type="number" min="0" placeholder="Limit (GB)"
               value={limitGb} onChange={(e) => setLimitGb(e.target.value)} />
        <input className="bz-input bz-num" type="number" min="0" max="100" placeholder="Warn at %"
               value={warnPct} onChange={(e) => setWarnPct(e.target.value)} />
        <button className="bz-btn" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
      </div>
    </>
  )
}

const SERVICES = [
  { name: 'Supabase (Sydney)', what: 'Auth, database, storage — project mgqnfvgehtybutzgbgxz, ap-southeast-2', url: 'https://supabase.com/dashboard/project/mgqnfvgehtybutzgbgxz' },
  { name: 'Cloudflare Pages', what: 'App hosting — project "bester"', url: 'https://dash.cloudflare.com/?to=/:account/pages/view/bester' },
  { name: 'R2 storage', what: 'Song audio/video media', url: 'https://dash.cloudflare.com/?to=/:account/r2/overview' },
  { name: 'Sentry', what: 'Error monitoring', url: 'https://beseter.sentry.io' },
]

function HealthCard({ overview }) {
  return (
    <>
      <p className="bz-lede">
        <b>{fmtGb(overview?.storage_bytes)}</b> total storage · <b>{overview?.songs_total ?? 0}</b> songs
        {overview?.songs_removed ? <> · <b className="bad">{overview.songs_removed}</b> removed</> : null}
      </p>
      {SERVICES.map((s) => (
        <div key={s.name} className="bz-ledgerrow">
          <span className="bz-ledgerdesc"><b>{s.name}</b> — {s.what}</span>
          <a className="bz-btn" href={s.url} target="_blank" rel="noreferrer">Open</a>
        </div>
      ))}
    </>
  )
}

function SentryCard() {
  return (
    <>
      <p className="bz-lede">Error monitoring is wired and live.</p>
      <p className="bz-meta">
        A Sentry MCP connection can pull issues straight into a session instead of tab-switching —
        not set up yet. <b>Stub card</b>, another session fills this in.
      </p>
      <a className="bz-btn" href="https://beseter.sentry.io" target="_blank" rel="noreferrer">Open Sentry</a>
    </>
  )
}

function ChurchesCard({ churches }) {
  if (churches.length === 0) return <p className="bz-meta">No churches yet.</p>
  return (
    <div className="bz-rows">
      {churches.map((c) => (
        <div key={c.id} className="bz-row">
          <span className="bz-badge">{(c.name || '?').slice(0, 2)}</span>
          <span className="bz-rowbody">
            <span className="bz-rowtitle">
              {c.name}{[c.city, c.state].filter(Boolean).length ? ` — ${[c.city, c.state].filter(Boolean).join(', ')}` : ''}
            </span>
            <span className="bz-rowsub">
              {c.songs_received} received · {c.songs_waiting} waiting · {c.songs_decided} decided
            </span>
          </span>
          <span className="bz-rowend">
            {c.reviewers === 0
              ? <span className="bz-tag bad">no reviewer</span>
              : <span className="bz-tag good">{c.reviewers} reviewer{c.reviewers === 1 ? '' : 's'}</span>}
          </span>
        </div>
      ))}
    </div>
  )
}

function PeopleCard({ people }) {
  if (people.length === 0) return <p className="bz-meta">No users yet.</p>
  return (
    <div className="bz-rows">
      {people.slice(0, 30).map((u) => {
        const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email
        return (
          <div key={u.id} className="bz-row">
            <span className="bz-badge">{name.slice(0, 2)}</span>
            <span className="bz-rowbody">
              <span className="bz-rowtitle">{name} — {u.role}</span>
              <span className="bz-rowsub">
                {u.songs_written} songs · {u.memberships} churches
                {u.last_song_at ? ` · last song ${ago(u.last_song_at)}` : ''}
              </span>
            </span>
            <span className="bz-rowend">
              {u.suspended_at && <span className="bz-tag bad">suspended</span>}
              {u.songs_written > 0 && u.memberships === 0 && (
                <span className="bz-tag bad">no church</span>
              )}
            </span>
          </div>
        )
      })}
    </div>
  )
}

function InboxCard({ feedbackOpen, requestsWaiting, membersPending }) {
  return (
    <>
      <div className="bz-ledgerrow">
        <span className="bz-ledgerdesc">Open feedback</span>
        <span className={feedbackOpen ? 'bad' : 'good'}>{feedbackOpen}</span>
        <a className="bz-btn" href="/admin/feedback">Open</a>
      </div>
      <div className="bz-ledgerrow">
        <span className="bz-ledgerdesc">Publishing requests waiting</span>
        <span className={requestsWaiting ? 'bad' : 'good'}>{requestsWaiting}</span>
        <a className="bz-btn" href="/admin/requests">Open</a>
      </div>
      <div className="bz-ledgerrow">
        <span className="bz-ledgerdesc">Pending church members</span>
        <span className={membersPending ? 'bad' : 'good'}>{membersPending}</span>
      </div>
    </>
  )
}

function BragCard({ overview }) {
  if (!overview) return <p className="bz-meta">Nothing to brag about yet.</p>
  const lines = [
    `${overview.songs_total} songs written`,
    `${overview.churches_total} churches`,
    `${overview.songs_new_7d} songs in the last 7 days`,
  ]
  const copy = () => navigator.clipboard?.writeText(lines.join(' · '))
  return (
    <>
      <p className="bz-lede">{lines.join(' · ')}</p>
      <p className="bz-meta">
        Duration of music isn't shown — storage bytes don't reliably convert to minutes across
        formats, and a guessed number here would be worse than none.
      </p>
      <button className="bz-btn" onClick={copy}>Copy</button>
    </>
  )
}

function RetentionCard({ rows }) {
  if (rows.length === 0) return null
  return rows.map((r) => (
    <div key={`${r.category}-${r.record_id}`} className="bz-ledgerrow">
      <span className="bz-ledgerdesc"><b>{r.label}</b> — {r.why}</span>
      <span className="bz-meta">since {ago(r.since)}</span>
    </div>
  ))
}

function AbuseCard({ rows }) {
  if (rows.length === 0) return null
  return rows.map((r, i) => (
    <div key={`${r.signal}-${r.subject_id}-${i}`} className="bz-ledgerrow">
      <span className="bz-ledgerdesc"><b>{r.subject}</b> — {r.signal}: {r.detail}</span>
      <span className="bz-tag bad">weight {r.weight}</span>
    </div>
  ))
}

/* ------------------------------------------------------------
   Executive brief — rules-based, not a model. It only ever reads
   aggregate business counts and money totals that are ALREADY loaded
   for the other cards (overview, business, storage settings, feedback/
   request/member counts). It must never touch song lyrics, song
   titles, or any individual's personal data — this is the one card an
   owner might screenshot or paste into a chat without re-checking what's
   in it, so keeping it structurally incapable of surfacing content or
   PII matters more here than anywhere else on the dashboard.
   ------------------------------------------------------------ */
function BriefCard({ findings }) {
  return (
    <>
      {findings.map((f, i) => (
        <div key={i} className="bz-ledgerrow">
          <span className={`bz-dot ${f.tone}`} />
          <span className="bz-ledgerdesc">{f.text}</span>
          {f.href && <a className="bz-btn" href={f.href}>Open</a>}
        </div>
      ))}
      <p className="bz-meta" style={{ marginTop: 10 }}>
        Business figures only — this reads counts and totals, never song content or personal details.
      </p>
    </>
  )
}

function buildBrief(data) {
  const findings = []
  const { overview, business, settings, feedbackOpen, requestsRequestedCount, requestsOldestDays } = data

  if (overview?.storage_bytes != null && n(settings.storage_limit_gb) > 0) {
    const used = gb(overview.storage_bytes)
    const limit = n(settings.storage_limit_gb)
    const pct = Math.round((used / limit) * 100)
    const warn = n(settings.storage_warn_pct) || 75
    if (pct >= warn) findings.push({ tone: 'bad', text: `Storage at ${pct}% of your ${limit} GB ceiling` })
  }

  const noReviewer = (data.churches ?? []).filter((c) => n(c.reviewers) === 0).length
  if (noReviewer > 0) {
    findings.push({ tone: 'bad', text: `${noReviewer} church${noReviewer === 1 ? '' : 'es'} have no reviewer — songs sent there cannot be decided` })
  }

  if (requestsRequestedCount > 0 && requestsOldestDays >= 7) {
    findings.push({ tone: 'bad', text: `${requestsRequestedCount} publishing request(s) waiting, oldest ${requestsOldestDays} days`, href: '/admin/requests' })
  }

  if (feedbackOpen > 0) {
    findings.push({ tone: 'warn', text: `${feedbackOpen} support report(s) unread`, href: '/admin/feedback' })
  }

  const membersPending = n(overview?.members_pending)
  if (membersPending > 0) {
    findings.push({ tone: 'warn', text: `${membersPending} ${membersPending === 1 ? 'person' : 'people'} waiting to join a church` })
  }

  if (business && n(business.paying_subscribers) > 0 && n(business.mrr_cents) === 0) {
    findings.push({ tone: 'bad', text: `${business.paying_subscribers} paying subscriber(s) but no plan price set — revenue reads zero` })
  }

  if (business) {
    const code = business.currency || 'AUD'
    const costsMtd = n(business.costs_mtd_cents)
    const revenueMtd = n(business.revenue_mtd_cents)
    if (costsMtd > revenueMtd) {
      findings.push({ tone: 'warn', text: `Costs are ${money(costsMtd - revenueMtd, code)} ahead of revenue this month` })
    }
    const runRate = n(business.monthly_run_rate_cents)
    const mrr = n(business.mrr_cents)
    if (runRate > mrr) {
      findings.push({ tone: 'warn', text: `Run rate ${money(runRate, code)}/mo exceeds MRR ${money(mrr, code)}/mo` })
    }
  }

  const inReview = n(overview?.songs_in_review)
  if (inReview > 0) {
    findings.push({ tone: 'good', text: `${inReview} song(s) with a reviewer right now` })
  }

  if (findings.length === 0) {
    findings.push({ tone: 'good', text: 'Nothing needs you right now.' })
  }

  return findings
}

/* Default order. Anything a user has never touched falls back to this.
   The list itself has no visual meaning — render order is the fixed
   group order (money, growth, operations, risk) with this only breaking
   ties inside a group — but `brief` is listed first here so a fresh
   account merges it at the front of its (risk) group. */
const DEFAULT_ORDER = [
  'brief', 'overview', 'money', 'inbox', 'ledger', 'forecast', 'storage', 'ops', 'churches', 'people',
  'health', 'brag', 'log', 'retention', 'abuse', 'sentry',
]

export default function Business() {
  const { user } = useSession()
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [layout, setLayout] = useState(null) // [{id, hidden}]
  const [collapsedGroups, setCollapsedGroups] = useState([])
  const [minimised, setMinimised] = useState([])
  const [arranging, setArranging] = useState(false)
  const [dragId, setDragId] = useState(null)
  const [dropId, setDropId] = useState(null)

  const load = useCallback(async () => {
    setErr(null)
    const [overview, business, churches, people, retention, abuse, feedback, requests, prefs, businessLog, opsSnapshot] = await Promise.all([
      supabase.from('platform_overview').select('*').maybeSingle(),
      supabase.from('platform_business').select('*').maybeSingle(),
      supabase.from('platform_churches').select('*').order('created_at', { ascending: false }),
      supabase.from('platform_users').select('*').order('created_at', { ascending: false }),
      supabase.from('retention_review').select('*'),
      supabase.from('platform_abuse_signals').select('*').order('weight', { ascending: false }),
      supabase.from('feedback').select('id, status'),
      supabase.from('song_publishing_requests').select('id, status, requested_at'),
      supabase.from('platform_dashboard_prefs').select('layout').eq('user_id', user?.id).maybeSingle(),
      supabase.from('business_log').select('*').order('occurred_on', { ascending: false }).limit(30),
      supabase.from('ops_snapshot').select('observed_at, payload').order('observed_at', { ascending: false }).limit(1).maybeSingle(),
    ])
    const firstError = [overview, business, churches, people, retention, abuse, feedback, requests]
      .find((r) => r.error)
    if (firstError) setErr(readableError(firstError.error))

    // business_settings is key/value rows, not one row — folded into a flat
    // object so cards can read settings.plus_price_cents like any other field.
    const { data: settingsRows } = await supabase.from('business_settings').select('key, value')
    const settings = Object.fromEntries((settingsRows ?? []).map((r) => [r.key, r.value]))

    const { data: ledger } = await supabase.from('business_ledger')
      .select('*').order('entry_date', { ascending: false }).limit(20)

    // The forecast card needs EVERY recurring cost, not just the last 20
    // ledger rows — a yearly hosting bill entered in January would silently
    // drop out of the run rate the moment 20 newer entries land.
    const { data: recurringCosts } = await supabase.from('business_ledger')
      .select('*').eq('kind', 'cost').neq('recurring', 'once').order('amount_cents', { ascending: false })

    const requestedRows = (requests.data ?? []).filter((r) => r.status === 'requested')
    const requestsOldestDays = requestedRows.length
      ? Math.max(...requestedRows.map((r) => daysAgo(r.requested_at) ?? 0))
      : 0

    setData({
      overview: overview.data ?? null,
      business: business.data ?? null,
      churches: churches.data ?? [],
      people: people.data ?? [],
      retention: retention.data ?? [],
      abuse: abuse.data ?? [],
      feedbackOpen: (feedback.data ?? []).filter((f) => f.status !== 'closed').length,
      requestsWaiting: (requests.data ?? []).filter((r) => r.status === 'requested' || r.status === 'in_progress').length,
      requestsRequestedCount: requestedRows.length,
      requestsOldestDays,
      settings,
      ledger: ledger ?? [],
      recurringCosts: recurringCosts ?? [],
      businessLog: businessLog.data ?? [],
      opsSnapshot: opsSnapshot.data ?? null,
    })

    // Saved prefs used to be a bare array of {id, hidden}. Keep reading that
    // shape forever — a stored value is a stored value, and rewriting the
    // meaning of an existing column out from under it breaks silently for
    // anyone who saved a layout before this shipped.
    const rawPrefs = prefs.data?.layout
    const savedCards = Array.isArray(rawPrefs) ? rawPrefs : (rawPrefs?.cards ?? [])
    const savedCollapsed = Array.isArray(rawPrefs) ? [] : (rawPrefs?.collapsedGroups ?? [])
    const savedMinimised = Array.isArray(rawPrefs) ? [] : (rawPrefs?.minimised ?? [])

    // Merge saved layout with the card registry: known ids keep their saved
    // order, unknown ids (a card built after the prefs row was saved) append
    // visible at the end rather than vanishing.
    const savedIds = new Set(savedCards.map((s) => s.id))
    const merged = [
      ...savedCards.filter((s) => DEFAULT_ORDER.includes(s.id)),
      ...DEFAULT_ORDER.filter((id) => !savedIds.has(id)).map((id) => ({ id, hidden: false })),
    ]
    setLayout(merged)
    setCollapsedGroups(savedCollapsed)
    setMinimised(savedMinimised)
  }, [user?.id])

  useEffect(() => { load() }, [load])

  // Single write path for every prefs mutation (reorder, hide, minimise,
  // collapse) so the upsert always carries the full {cards, collapsedGroups,
  // minimised} shape — no partial-save race between the three pieces of state.
  const persist = useCallback(async (next) => {
    setLayout(next.cards)
    setCollapsedGroups(next.collapsedGroups)
    setMinimised(next.minimised)
    if (!user?.id) return
    await supabase.from('platform_dashboard_prefs')
      .upsert({ user_id: user.id, layout: next, updated_at: new Date().toISOString() }, { onConflict: 'user_id' })
  }, [user?.id])

  const resetLayout = async () => {
    const next = { cards: DEFAULT_ORDER.map((id) => ({ id, hidden: false })), collapsedGroups: [], minimised: [] }
    setLayout(next.cards)
    setCollapsedGroups(next.collapsedGroups)
    setMinimised(next.minimised)
    if (user?.id) await supabase.from('platform_dashboard_prefs').delete().eq('user_id', user.id)
  }

  const move = (id, dir) => {
    const i = layout.findIndex((c) => c.id === id)
    const j = i + dir
    if (j < 0 || j >= layout.length) return
    const next = [...layout]
    ;[next[i], next[j]] = [next[j], next[i]]
    persist({ cards: next, collapsedGroups, minimised })
  }

  // Drag reorders within the full layout array. Rendering then re-filters by
  // group, so this only visibly moves a card within its own section unless
  // dragged across a section boundary — good enough without tracking group
  // membership as a separate axis of the stored order.
  const reorder = (draggedId, targetId) => {
    if (draggedId === targetId) return
    const from = layout.findIndex((c) => c.id === draggedId)
    const to = layout.findIndex((c) => c.id === targetId)
    if (from < 0 || to < 0) return
    const next = [...layout]
    const [item] = next.splice(from, 1)
    next.splice(to, 0, item)
    persist({ cards: next, collapsedGroups, minimised })
  }

  const toggleHidden = (id) => {
    persist({ cards: layout.map((c) => (c.id === id ? { ...c, hidden: !c.hidden } : c)), collapsedGroups, minimised })
  }

  const toggleMinimised = (id) => {
    const next = minimised.includes(id) ? minimised.filter((m) => m !== id) : [...minimised, id]
    persist({ cards: layout, collapsedGroups, minimised: next })
  }

  const toggleGroupCollapsed = (groupId) => {
    const next = collapsedGroups.includes(groupId)
      ? collapsedGroups.filter((g) => g !== groupId)
      : [...collapsedGroups, groupId]
    persist({ cards: layout, collapsedGroups: next, minimised })
  }

  const addLedger = async (entry) => {
    const { error } = await supabase.from('business_ledger').insert({ ...entry, created_by: user?.id })
    if (!error) load()
    return { error }
  }
  const deleteLedger = async (id) => {
    await supabase.from('business_ledger').delete().eq('id', id)
    load()
  }
  const addLog = async (entry) => {
    const { error } = await supabase.from('business_log').insert({ ...entry, created_by: user?.id })
    if (!error) load()
    return { error }
  }
  const deleteLog = async (id) => {
    await supabase.from('business_log').delete().eq('id', id)
    load()
  }
  const saveSettingsValue = async (key, value) => {
    await supabase.from('business_settings').upsert({ key, value }, { onConflict: 'key' })
    load()
  }

  // Every card body reads only from `data`, so the registry can be built once
  // per render without threading loaders through each component.
  const CARDS = useMemo(() => {
    if (!data) return []
    const currency = data.business?.currency || data.settings.currency || 'AUD'
    return [
      { id: 'brief', group: 'risk', title: 'What needs you', blurb: 'A rules-based briefing from figures already on this screen.', render: () => <BriefCard findings={buildBrief(data)} /> },
      { id: 'overview', group: 'growth', title: 'Overview', blurb: 'The headline numbers.', render: () => <OverviewCard overview={data.overview} /> },
      { id: 'money', group: 'money', title: 'Money', blurb: 'MRR, revenue, costs, run rate.', render: () => (
        <MoneyCard business={data.business} settings={data.settings}
                   onSetPrice={(cents) => saveSettingsValue('plus_price_cents', cents)} />
      ) },
      { id: 'inbox', group: 'operations', title: 'Needs you', blurb: 'One place to see what needs a decision.', render: () => (
        <InboxCard feedbackOpen={data.feedbackOpen} requestsWaiting={data.requestsWaiting}
                   membersPending={data.overview?.members_pending ?? 0} />
      ) },
      { id: 'ledger', group: 'money', title: 'Ledger', blurb: 'Add and review revenue/cost entries.', render: () => (
        <LedgerCard entries={data.ledger} currency={currency} onAdd={addLedger} onDelete={deleteLedger} />
      ) },
      { id: 'forecast', group: 'money', title: 'Cost planning', blurb: 'Run rate, margin, and a flat 12-month projection.', render: () => (
        <ForecastCard recurringCosts={data.recurringCosts} currency={currency} mrrCents={data.business?.mrr_cents} />
      ) },
      { id: 'storage', group: 'operations', title: 'Storage', blurb: 'Usage against the limit.', render: () => (
        <StorageCard overview={data.overview} settings={data.settings}
                     onSaveLimits={async ({ storage_limit_gb, storage_warn_pct }) => {
                       await Promise.all([
                         saveSettingsValue('storage_limit_gb', storage_limit_gb),
                         saveSettingsValue('storage_warn_pct', storage_warn_pct),
                       ])
                     }} />
      ) },
      { id: 'ops', group: 'operations', title: 'Operations', blurb: 'Edge functions, cron jobs, migrations, database size.', render: () => <OpsCard snapshot={data.opsSnapshot} /> },
      { id: 'log', group: 'growth', title: 'Business log', blurb: 'Registration, tax, legal, and other company events.', render: () => (
        <LogCard entries={data.businessLog} onAdd={addLog} onDelete={deleteLog} />
      ) },
      { id: 'churches', group: 'growth', title: 'Churches', blurb: 'Every church, reviewer coverage.', render: () => <ChurchesCard churches={data.churches} /> },
      { id: 'people', group: 'growth', title: 'People', blurb: 'Recent signups first.', render: () => <PeopleCard people={data.people} /> },
      { id: 'health', group: 'operations', title: 'System', blurb: 'Storage and the external services.', render: () => <HealthCard overview={data.overview} /> },
      { id: 'brag', group: 'growth', title: 'Numbers worth saying out loud', blurb: 'Pitch stats, ready to copy.', render: () => <BragCard overview={data.overview} /> },
      { id: 'retention', group: 'risk', title: 'Retention review', blurb: 'Records due a retention decision.', render: () => <RetentionCard rows={data.retention} /> },
      { id: 'abuse', group: 'risk', title: 'Abuse signals', blurb: 'Worst first.', render: () => <AbuseCard rows={data.abuse} /> },
      { id: 'sentry', group: 'operations', title: 'Errors', blurb: 'Sentry — stub.', render: () => <SentryCard /> },
    ].filter((c) => {
      // retention/abuse render nothing when empty — don't show an empty card shell for them.
      if (c.id === 'retention') return data.retention.length > 0
      if (c.id === 'abuse') return data.abuse.length > 0
      return true
    })
  }, [data])

  if (data === null || layout === null) {
    return <PortalFrame title="Business" sub="Loading…"><Spinner /></PortalFrame>
  }

  const byId = Object.fromEntries(CARDS.map((c) => [c.id, c]))
  const ordered = layout.map((l) => ({ ...l, card: byId[l.id] })).filter((l) => l.card)
  const visible = ordered.filter((l) => !l.hidden)
  const hidden = ordered.filter((l) => l.hidden)

  return (
    <PortalFrame
      title="Business"
      sub="One place for how it's actually going — money, growth, and what needs you."
      aside={(
        <div className="bz-actions">
          <button className="bz-btn" onClick={() => setArranging((a) => !a)}>
            {arranging ? 'Done arranging' : 'Arrange'}
          </button>
          {arranging && <button className="bz-btn bz-danger" onClick={resetLayout}>Reset to default</button>}
        </div>
      )}
    >
      {err && <p className="err">{err}</p>}

      {GROUPS.map((group) => {
        const groupCards = visible.filter((l) => l.card.group === group.id)
        if (groupCards.length === 0) return null
        const collapsed = collapsedGroups.includes(group.id)
        return (
          <div key={group.id} className="bz-section">
            <button className="bz-sectionhead" onClick={() => toggleGroupCollapsed(group.id)}>
              <span className="bz-sectiontitle">{group.title}</span>
              <span className="bz-sectiontoggle">{collapsed ? 'Show' : 'Hide'}</span>
            </button>
            {!collapsed && (
              <div className="bz-grid">
                {groupCards.map(({ id, card }, i) => {
                  const isMinimised = minimised.includes(id)
                  return (
                    <div
                      key={id}
                      className={`bz-card${dropId === id ? ' bz-dropzone' : ''}`}
                      draggable={arranging}
                      onDragStart={() => setDragId(id)}
                      onDragOver={(e) => { if (arranging) { e.preventDefault(); setDropId(id) } }}
                      onDragLeave={() => setDropId((d) => (d === id ? null : d))}
                      onDrop={(e) => {
                        e.preventDefault()
                        setDropId(null)
                        if (dragId) reorder(dragId, id)
                        setDragId(null)
                      }}
                    >
                      <div className="bz-cardhead">
                        <h4 className="bz-title">{card.title}</h4>
                        <button className="bz-btn" onClick={() => toggleMinimised(id)}>
                          {isMinimised ? 'Expand' : 'Minimise'}
                        </button>
                      </div>
                      {!isMinimised && (arranging ? (
                        <>
                          <p className="bz-meta">{card.blurb}</p>
                          <div className="bz-actions">
                            <button className="bz-btn" disabled={i === 0} onClick={() => move(id, -1)}>Move up</button>
                            <button className="bz-btn" disabled={i === groupCards.length - 1} onClick={() => move(id, 1)}>Move down</button>
                            <button className="bz-btn" onClick={() => toggleHidden(id)}>Hide</button>
                          </div>
                        </>
                      ) : card.render())}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}

      {hidden.length > 0 && (
        <>
          <h4 className="bz-title" style={{ marginTop: 24 }}>Hidden</h4>
          {hidden.map(({ id, card }) => (
            <div key={id} className="bz-ledgerrow">
              <span className="bz-ledgerdesc">{card.title}</span>
              <button className="bz-btn" onClick={() => toggleHidden(id)}>Show</button>
            </div>
          ))}
        </>
      )}
    </PortalFrame>
  )
}
